import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';

/**
 * Carte de décision (ADR-016 T4 ; ADR-080 §6) sur l'écran d'une tâche : la projection lue par
 * `GetDecisionCard` pour le sujet du Work Item. Lecture seule : elle présente une décision et les
 * commandes que la tâche permet déjà ; elle n'en ajoute aucune. Une décision restée en `shadow`
 * n'a pas de carte. La confiance ne s'affiche comme une probabilité que calibrée (ADR-071 §5).
 */
interface DecisionCardData {
  card: { decision_id: string; explanation: string; approval_state: string; policy_requirements?: string[] };
  explainability: {
    what: { decision_type: string; question: string; output: Record<string, unknown> };
    based_on: { subjects: Array<{ type: string; id: string }>; context: Array<{ ref_type: string; id: string }> };
    how: { mechanism: string; compute_level?: string; provider_id?: string; model_id?: string; model_version?: string; execution_location?: string };
    certainty: { semantics: string; display: 'probability' | 'provider_hint' | 'none'; value?: number; label?: string };
    policy: { decision_policy_ref: { id: string; version: string }; threshold_band?: string; routing?: string; autonomy_stage?: string };
    permitted: { commands: string[]; role?: string; note: string };
    history: Array<{ sequence: number; compute_level: string; provider_id?: string; router_decision: string; fallback?: string }>;
  };
}

function certaintyText(certainty: DecisionCardData['explainability']['certainty']): string {
  if (certainty.display === 'probability' && certainty.value !== undefined) return `Probabilité calibrée : ${Math.round(certainty.value * 100)} %`;
  if (certainty.display === 'provider_hint' && certainty.value !== undefined) return `Indice du fournisseur, non calibré : ${certainty.value}`;
  return 'Aucune mesure de certitude';
}

export function DecisionCardPanel({ subject }: { subject: { type: string; id: string } }) {
  const services = useAppServices();
  const [loaded] = useLoad<DecisionCardData | null>(`decision-card:${subject.type}:${subject.id}`, async () => (await services.client.query<DecisionCardData | null>('GetDecisionCard', { subject_type: subject.type, subject_id: subject.id })).data);
  if (loaded.status !== 'ready' || loaded.value === null) return null;
  const { card, explainability: x } = loaded.value;
  const output = Object.values(x.what.output)[0];
  return (
    <section className="ay-card" aria-labelledby="ay-decision-card-title">
      <h2 id="ay-decision-card-title">Avis de la décision assistée</h2>
      <p className="ay-text">
        <strong>{String(output ?? '—')}</strong> — {x.what.question}
      </p>
      <dl className="ay-detail">
        <dt>Sur quoi</dt>
        <dd>{[...x.based_on.subjects, ...x.based_on.context.map((ref) => ({ type: ref.ref_type, id: ref.id }))].map((ref) => ref.type).join(', ')}</dd>
        <dt>Comment</dt>
        <dd>
          {x.how.mechanism}
          {x.how.compute_level === undefined ? '' : ` (${x.how.compute_level})`}
          {x.how.provider_id === undefined ? '' : ` — ${x.how.provider_id} ${x.how.model_id ?? ''} ${x.how.model_version ?? ''}, ${x.how.execution_location ?? ''}`}
        </dd>
        <dt>Certitude</dt>
        <dd>{certaintyText(x.certainty)}</dd>
        <dt>Politique</dt>
        <dd>
          {x.policy.decision_policy_ref.id}@{x.policy.decision_policy_ref.version}, stade {x.policy.autonomy_stage ?? 'shadow'}
          {x.policy.threshold_band === undefined ? '' : `, bande ${x.policy.threshold_band}`}
        </dd>
        <dt>Ce qui est permis</dt>
        <dd>{x.permitted.commands.length === 0 ? x.permitted.note : `${x.permitted.commands.join(', ')} — ${x.permitted.note}`}</dd>
        <dt>Historique</dt>
        <dd>{x.history.map((step) => `${step.sequence}. ${step.compute_level}${step.provider_id === undefined ? '' : ` (${step.provider_id})`} → ${step.router_decision}${step.fallback === undefined ? '' : ` [repli : ${step.fallback}]`}`).join(' ; ')}</dd>
      </dl>
      <p className="ay-text ay-text--muted">{card.explanation}</p>
    </section>
  );
}
